/**
 * ImageSlotControl — Image picker with upload, library, and URL input.
 *
 * Uses AttachmentPickerDialog (same as product gallery) for upload + library browsing.
 * Preserves alt, styleClass, width, height, and appearance from the original slot value.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Upload, X } from 'lucide-react'
import { AttachmentPickerDialog } from '@/components/attachments/AttachmentPickerDialog'
import type { SlotValue } from '@zaviago/storefront-config'

interface ImageSlotControlProps {
  slotValue: SlotValue
  onChange: (v: SlotValue) => void
  label: string
}

function extractUrl(slot: SlotValue): string {
  if (typeof slot === 'string') return slot
  if ('value' in slot && slot.value) return slot.value
  if ('assetRef' in slot) return slot.assetRef
  return ''
}

/** Dummy context for upload-only mode (no doc attachment, just file upload) */
const UPLOAD_CONTEXT = { doctype: 'File', name: 'upload' } as const

export function ImageSlotControl({ slotValue, onChange, label }: ImageSlotControlProps) {
  const [url, setUrl] = useState(() => extractUrl(slotValue))
  const [pickerOpen, setPickerOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    setUrl(extractUrl(slotValue))
  }, [slotValue])

  const applyUrl = useCallback(
    (next: string) => {
      setUrl(next)
      // Store as { value: url } — storefront reads slots.image.value
      if (typeof slotValue === 'string') {
        onChange({ value: next })
      } else if ('value' in slotValue) {
        onChange({ ...slotValue, value: next })
      } else {
        onChange({ value: next })
      }
    },
    [slotValue, onChange],
  )

  const handleInputChange = (next: string) => {
    setUrl(next)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => applyUrl(next), 300)
  }

  const handleClear = () => applyUrl('')

  useEffect(() => {
    return () => clearTimeout(timerRef.current)
  }, [])

  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-medium text-gray-600">{label}</Label>

      {/* Thumbnail preview / empty state — clickable to open picker */}
      {url ? (
        <div className="group relative overflow-hidden rounded-xs border border-gray-200">
          <img
            src={url}
            alt="Preview"
            className="h-24 w-full object-cover"
            onError={(e) => {
              ;(e.target as HTMLImageElement).style.display = 'none'
            }}
          />
          {/* Overlay actions */}
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 gap-1 text-xs"
              onClick={() => setPickerOpen(true)}
            >
              <Upload className="h-3 w-3" />
              Replace
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="h-7 w-7 p-0"
              onClick={handleClear}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="flex h-24 w-full items-center justify-center gap-2 rounded-xs border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-400 transition-colors hover:border-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <Upload className="h-4 w-4" />
          Upload or choose image
        </button>
      )}

      {/* URL input (collapsed, for pasting URLs directly) */}
      <Input
        value={url}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder="Or paste image URL..."
        className="text-xs"
      />

      {/* Attachment picker dialog (upload, library, link, camera) */}
      <AttachmentPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        context={UPLOAD_CONTEXT}
        accept={['image/*']}
        mode="upload-only"
        onCompleted={(result) => {
          const fileUrl = result?.attachments?.[0]?.file_url
          if (fileUrl) {
            applyUrl(fileUrl)
          }
          setPickerOpen(false)
        }}
      />
    </div>
  )
}
